import { useState, useEffect, useCallback, useRef } from 'react'
import { LatexText } from '../../../components/ui/LatexText'

interface ClozeOption {
  label: string
  content: string
}

interface ClozeBlank {
  index: number
  questionId: string
  value: string                    // selected option label (e.g. 'C')
  options: ClozeOption[] | null    // A/B/C/D options (null = legacy fill_blank)
  onChange: (value: string) => void
  readOnly?: boolean
  isCorrect?: boolean | null
  correctAnswer?: string | null
}

interface ClozeRendererProps {
  passage: string
  blanks: ClozeBlank[]
  className?: string
  /** Called when user selects an option for a blank */
  onBlankSelect?: (blankIndex: number) => void
}

/**
 * Renders a cloze (完形填空) passage with clickable choice blanks.
 * Click a blank → expand options panel → click option to select.
 * Supports Tab navigation between blanks.
 */
export function ClozeRenderer({ passage, blanks, className = '', onBlankSelect }: ClozeRendererProps) {
  const [activeBlank, setActiveBlank] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Find blank by index
  const findBlank = (num: number) => blanks.find(b => b.index === num)

  // Select option and auto-advance
  const handleSelect = useCallback((blankNum: number, label: string) => {
    const blank = findBlank(blankNum)
    if (!blank || blank.readOnly) return
    blank.onChange(label)

    // Auto-advance to next unanswered blank
    const sortedBlanks = [...blanks].sort((a, b) => a.index - b.index)
    const nextUnanswered = sortedBlanks.find(b => b.index > blankNum && !b.value)
    if (nextUnanswered) {
      setActiveBlank(nextUnanswered.index)
    } else {
      // Wrap around: find first unanswered before current
      const prevUnanswered = sortedBlanks.find(b => b.index < blankNum && !b.value)
      if (prevUnanswered) {
        setActiveBlank(prevUnanswered.index)
      } else {
        setActiveBlank(null) // all answered
      }
    }
    onBlankSelect?.(blankNum)
  }, [blanks])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeBlank === null) return
      const sortedBlanks = [...blanks].sort((a, b) => a.index - b.index)
      const currentIdx = sortedBlanks.findIndex(b => b.index === activeBlank)

      if (e.key === 'Tab') {
        e.preventDefault()
        if (e.shiftKey) {
          // Previous blank
          const prev = currentIdx > 0 ? sortedBlanks[currentIdx - 1] : sortedBlanks[sortedBlanks.length - 1]
          setActiveBlank(prev.index)
        } else {
          // Next blank
          const next = currentIdx < sortedBlanks.length - 1 ? sortedBlanks[currentIdx + 1] : sortedBlanks[0]
          setActiveBlank(next.index)
        }
      } else if (e.key === 'Escape') {
        setActiveBlank(null)
      } else if (/^[a-dA-D]$/.test(e.key) && !e.ctrlKey && !e.metaKey) {
        // A/B/C/D key to select option
        const blank = findBlank(activeBlank)
        if (blank && blank.options && !blank.readOnly) {
          const label = e.key.toUpperCase()
          const hasOption = blank.options.some(o => o.label === label)
          if (hasOption) {
            handleSelect(activeBlank, label)
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activeBlank, blanks])

  // Split passage into paragraphs, then split each paragraph by blank markers
  const paragraphs = passage.split(/\n{2,}/)

  const renderPart = (part: string, key: React.Key) => {
    const blankMatch = part.match(/^__(\d+)__$/)
    if (blankMatch) {
      const blankNum = parseInt(blankMatch[1], 10)
      const blank = findBlank(blankNum)
      if (blank) {
        return (
          <ClozeBlankComponent
            key={`blank-${blankNum}`}
            blank={blank}
            isActive={activeBlank === blankNum}
            onActivate={() => setActiveBlank(blankNum === activeBlank ? null : blankNum)}
            onSelect={(label) => handleSelect(blankNum, label)}
            onClose={() => setActiveBlank(null)}
          />
        )
      }
    }
    return <LatexText key={key} inline>{part}</LatexText>
  }

  return (
    <div ref={containerRef} className={`text-base text-slate-800 leading-relaxed tracking-wide ${className}`}>
      {paragraphs.map((para, pIdx) => {
        const paraParts = para.split(/(__\d+__)/g)
        return (
          <p key={pIdx} className="mb-5 last:mb-0">
            {paraParts.map((part, i) => renderPart(part, `${pIdx}-${i}`))}
          </p>
        )
      })}
    </div>
  )
}

function ClozeBlankComponent({
  blank,
  isActive,
  onActivate,
  onSelect,
  onClose,
}: {
  blank: ClozeBlank
  isActive: boolean
  onActivate: () => void
  onSelect: (label: string) => void
  onClose: () => void
}) {
  const hasOptions = blank.options && blank.options.length > 0
  const selectedOption = hasOptions
    ? blank.options!.find(o => o.label === blank.value)
    : null

  // Determine styling based on grading result
  let bgColor = 'bg-blue-50 border-blue-300'
  let textColor = 'text-blue-700'
  if (blank.isCorrect === true) {
    bgColor = 'bg-green-50 border-green-400'
    textColor = 'text-green-700'
  } else if (blank.isCorrect === false) {
    bgColor = 'bg-red-50 border-red-400'
    textColor = 'text-red-700'
  }

  return (
    <span className="inline mx-0.5 align-baseline">
      {/* The blank button */}
      <button
        type="button"
        onClick={onActivate}
        className={[
          'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border-2 transition-all cursor-pointer',
          blank.value ? bgColor : 'bg-slate-50 border-slate-300 hover:border-indigo-300 hover:bg-indigo-50/50 hover:-translate-y-px hover:shadow-md',
          isActive ? 'ring-2 ring-indigo-400 ring-offset-1 shadow-sm' : '',
        ].filter(Boolean).join(' ')}
      >
        {blank.value && selectedOption ? (
          <span className={`text-sm font-medium ${textColor}`}>
            {selectedOption.content}
          </span>
        ) : (
          <span className="text-sm text-slate-400 min-w-[3rem] text-center">
            ({blank.index})
          </span>
        )}
        <sup className="text-[10px] text-slate-400">{blank.index}</sup>
      </button>

      {/* Options panel — shown when active and has options */}
      {isActive && hasOptions && (() => {
        // Use vertical layout for long options (e.g. seven_choose_five sentences)
        const isLongOptions = blank.options!.some(o => o.content.length > 20)
        return (
          <span
            className={[
              isLongOptions
                ? 'block mt-2 ml-0 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 space-y-1.5'
                : 'inline-flex items-center gap-1.5 ml-2 align-baseline px-2 py-1 rounded-lg bg-amber-50 border border-amber-200',
            ].join(' ')}
            onClick={(e) => e.stopPropagation()}
          >
            {blank.options!.map((opt) => {
              const isSelected = blank.value === opt.label
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => onSelect(opt.label)}
                  disabled={blank.readOnly}
                  className={[
                    isLongOptions
                      ? 'flex w-full items-start gap-2 px-3 py-2 rounded-lg text-sm transition-all border text-left'
                      : 'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm transition-all border',
                    isSelected
                      ? 'border-emerald-500 bg-emerald-100 text-emerald-800 font-medium shadow-sm'
                      : 'border-slate-200 bg-white hover:bg-slate-50 hover:shadow-sm text-slate-700',
                    blank.readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                  ].filter(Boolean).join(' ')}
                >
                  <span className={[
                    'inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    isSelected ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500',
                  ].join(' ')}>
                    {opt.label}
                  </span>
                  <span className={isLongOptions ? 'leading-snug' : 'whitespace-nowrap'}>{opt.content}</span>
                </button>
              )
            })}
          </span>
        )
      })()}

      {/* Legacy fill_blank mode (no options) */}
      {isActive && !hasOptions && (
        <span className="inline-block ml-1 align-baseline">
          <input
            type="text"
            value={blank.value}
            onChange={(e) => blank.onChange(e.target.value)}
            disabled={blank.readOnly}
            autoFocus
            className="w-32 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder={`(${blank.index})`}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
          />
        </span>
      )}
    </span>
  )
}
